import { gql, useQuery } from '@apollo/client';

import type {
  AdminListAssistantMemoryJournalQuery,
  AdminListAssistantMemoryJournalQueryVariables,
} from './__generated__/AssistantMemoryJournal.gql';

export const ADMIN_LIST_ASSISTANT_MEMORY_JOURNAL = gql`
  query AdminListAssistantMemoryJournal($userId: ID!, $memoryId: ID) {
    adminListAssistantMemoryJournal(userId: $userId, memoryId: $memoryId) {
      hasMore
      entries {
        id
        scope
        memoryId
        operation
        actor
        actorUserId
        sourceThreadId
        rationale
        createdAt
        before {
          body
          importance
          tags
        }
        after {
          body
          importance
          tags
        }
      }
    }
  }
`;

/**
 * The journal entries for one memory row, newest first.
 *
 * @param variables.userId - The memory's owner; the read is scoped to them.
 * @param variables.memoryId - The row whose history to load. The query is
 *   skipped while either argument is null, since a call without an id would
 *   read the user's whole journal and no surface here wants that.
 * @returns The entries, whether the per-scope cap was hit (`hasMore`), the
 *   loading flag, and the query error if one occurred. A caller must render
 *   `error` distinctly: an empty `entries` on a failed read is indistinguishable
 *   from a row that genuinely has no history.
 */
export function useAssistantMemoryJournal(variables: { userId: string | null; memoryId: string | null }) {
  const { userId, memoryId } = variables;
  const skip = userId === null || memoryId === null;
  const { data, loading, error } = useQuery<
    AdminListAssistantMemoryJournalQuery,
    AdminListAssistantMemoryJournalQueryVariables
  >(ADMIN_LIST_ASSISTANT_MEMORY_JOURNAL, {
    variables: { userId: userId ?? '', memoryId },
    skip,
    // An operator opening a history has just acted on the row, so a cached
    // listing from before that write is the one answer that misleads.
    fetchPolicy: 'network-only',
  });
  return {
    entries: data?.adminListAssistantMemoryJournal.entries ?? [],
    hasMore: data?.adminListAssistantMemoryJournal.hasMore ?? false,
    loading: loading && !skip,
    error,
  };
}
